import React, {useEffect, useMemo, useState} from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {StepConfigurationViewTemplateProps} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  DashboardAndBlockSelectionState,
  DashboardAndBlockSelectorFormGroupProps,
} from 'insights/analytics-queries/block-selector/DashboardAndBlockSelectorFormGroup';
import {runWarehouseQuery} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import areParameterValuesValid from 'insights/parameters/data/areParameterValuesValid';
import getParameterValueObject from 'insights/parameters/data/getParameterValueObject';
import {
  ParameterValueFieldListProps,
  ParameterValuesState,
} from 'insights/parameters/ParameterValueFieldList';
import {ROW_LIMIT} from 'insights/warehouse/AnalyticsWarehouseConstants';
import {RunQueryData} from 'insights/warehouse/AnalyticsWarehouseDAO';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus, NOT_FETCHED_ASYNC_STATE, getMostRecentData} from 'ui-library/hooks/useAsyncState';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import pickDefined from 'util/pickDefined';
import {MakeFieldsRequired} from 'util/Types';

import ChooseDifferentDataFrameCreationMethodButton from './ChooseDifferentDataFrameCreationMethodButton';
import {
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
  getTemplateDataFrameColumns,
} from './column-mapping/ColumnMappingUtil';
import CreateDataFrameFromAnalyticsBlockViewSkeleton, {
  FormState,
  makeSharedParameterValueFieldListProps,
  PersistedRunQueryData,
} from './CreateDataFrameFromAnalyticsBlockViewSkeleton';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from './DataFrameCreationConstants';
import * as Messages from './DataFrameCreationMessages';
import {DataFrameNameState} from './DataFrameCreationTypes';
import {
  getPersistedRunQueryData,
  getSharedDataFrameCreationMethodProps,
  openSelectedItemForNewStep,
} from './DataFrameCreationUtils';
import useAnalyticsBlockQuery from './hooks/useAnalyticsBlockQuery';
import {useAutoUpdatePreview} from './hooks/useAutoUpdatePreview';
import {DataFrameNameFieldProps} from './steps/DataFrameNameField';

// Because previews are generated using Insights' `runWarehouseQuery`, use Insights' row limit as the preview
// row limit.
export const PREVIEW_DATA_FRAME_ROW_LIMIT = ROW_LIMIT;
// Keep this constant in sync with `_CREATE_DATA_FRAME_FROM_SQL_QUERY_ROW_LIMIT` in
// `create_persisted_data_frame_from_untrusted_sql_query`.
export const FINAL_DATA_FRAME_ROW_LIMIT = 50000;

function getErrorAndWarningMessages(previewAsyncState: RunQueryState): Array<InlineBannerProps> {
  const messageList: Array<InlineBannerProps> = [];

  if (previewAsyncState.status === AsyncStatus.ERROR) {
    messageList.push(
      Messages.buildPreviewFailureMessage("Check your parameters' values or the selected block's query.")
    );
  }

  if (previewAsyncState.status === AsyncStatus.SUCCESS && previewAsyncState.data.isTruncated) {
    messageList.push(
      Messages.buildPreviewOverRowLimitMessage(
        PREVIEW_DATA_FRAME_ROW_LIMIT,
        `The final table will be limited to the first ${FINAL_DATA_FRAME_ROW_LIMIT.toLocaleString()} rows.`
      )
    );
  }

  return messageList;
}

const DEFAULT_DASHBOARD_AND_BLOCK_STATE: DashboardAndBlockSelectionState = {
  analyticsDashboardId: null,
  analyticsQueryId: null,
};
const DEFAULT_DATA_FRAME_NAME_STATE: DataFrameNameState = {name: '', hasUserEdits: false};
const DEFAULT_PARAMETER_VALUES_STATE: ParameterValuesState = {};

const dashboardAndBlockGlobalSelectionState = makeUseGlobalClientState<DashboardAndBlockSelectionState>(
  DEFAULT_DASHBOARD_AND_BLOCK_STATE
);
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);
const parameterValuesGlobalState = makeUseGlobalClientState<ParameterValuesState>(
  DEFAULT_PARAMETER_VALUES_STATE
);

const previewAsyncGlobalState = makeUseGlobalClientState<RunQueryState>(NOT_FETCHED_ASYNC_STATE);
const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

/**
 * Use a `FormState` object with state and state setters based on the global client state.
 *
 * @package
 */
export function useFormState(
  clientStateId: string
): MakeFieldsRequired<
  FormState,
  'setDashboardAndBlockSelectionState' | 'setDataFrameNameState' | 'setParameterValuesState' | 'clearState'
> {
  const [dashboardAndBlockSelectionState, setDashboardAndBlockSelectionState] =
    dashboardAndBlockGlobalSelectionState.useGlobalClientState(clientStateId);
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);
  const [parameterValuesState, setParameterValuesState] =
    parameterValuesGlobalState.useGlobalClientState(clientStateId);

  const clearState = (): void => {
    setDashboardAndBlockSelectionState(DEFAULT_DASHBOARD_AND_BLOCK_STATE);
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setParameterValuesState(DEFAULT_PARAMETER_VALUES_STATE);
  };

  return {
    dashboardAndBlockSelectionState,
    dataFrameNameState,
    parameterValuesState,
    setDashboardAndBlockSelectionState,
    setDataFrameNameState,
    setParameterValuesState,
    clearState,
  };
}

/**
 * Convert PersistedRunQueryData to RunQueryData by removing the parameter config id
 */
function getInsightsRunQueryData(persistedRunQueryData: PersistedRunQueryData | null): RunQueryData | null {
  return persistedRunQueryData == null
    ? null
    : {
        ...persistedRunQueryData,
        parameters: persistedRunQueryData.parameters.map(({config, valueJson}) => ({
          config: pickDefined(config, 'isMulti', 'systemName', 'type'),
          valueJson,
        })),
      };
}

export default function CreateDataFrameFromAnalyticsBlockView(
  props: MainViewComponentProps<AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION>
): JSX.Element {
  const formState = useFormState(props.clientStateId);
  const analyticsQueryInfo = useAnalyticsBlockQuery(formState.dashboardAndBlockSelectionState);
  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(props.clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(props.clientStateId);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  // Variables for `finalize` function
  const debouncedParameterValueState = useDebounce(formState.parameterValuesState, 300);
  const parameterValues =
    analyticsQueryInfo?.parameterConfigs.map((config) =>
      getParameterValueObject(config, debouncedParameterValueState[config.id] ?? null)
    ) ?? [];

  const persistedRunQueryData =
    analyticsQueryInfo &&
    getPersistedRunQueryData({
      sqlQuery: analyticsQueryInfo.sqlQuery,
      parameterConfigs: analyticsQueryInfo.parameterConfigs,
      parameterValues,
    });

  async function finalize(): Promise<void> {
    assert(persistedRunQueryData != null, 'Expected query to be defined.');
    assert(
      formState.dashboardAndBlockSelectionState.analyticsDashboardId != null,
      'Expected dashboard to be defined.'
    );
    assert(
      formState.dashboardAndBlockSelectionState.analyticsQueryId != null,
      'Expected block to be defined.'
    );

    try {
      setIsFinalizing(true);
      const newStep = await addOrReplaceAndRunAnalysisStep(
        props.analysis.id,
        {
          stepType: PipelineStepType.INITIALIZE_DATASET,
          stepVersion: 2,
          stepConfig: {
            dataFrameName: formState.dataFrameNameState.name,
            dashboardId: formState.dashboardAndBlockSelectionState.analyticsDashboardId,
            blockId: formState.dashboardAndBlockSelectionState.analyticsQueryId,
            parameters: persistedRunQueryData.parameters,
            parameterizedSQLQuery: persistedRunQueryData.parameterizedSQLQuery,
          },
        },
        props.selectedItem.stepIdToReplace
          ? {
              originalStepId: props.selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      openSelectedItemForNewStep(newStep, props.onCloseStepInitializationView);
      formState.clearState();
    } finally {
      setIsFinalizing(false);
    }
  }

  // Hook to generate data frame preview
  function generatePreview(data: RunQueryData, setAsyncState: (nextState: RunQueryState) => void): void {
    runWarehouseQuery(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
  }

  const runQueryData = getInsightsRunQueryData(persistedRunQueryData);

  useAutoUpdatePreview(runQueryData, generatePreview, (nextState) => {
    setPreviewState(nextState);
  });

  // Hook to update `dataFrameNameState`
  useEffect(() => {
    if (formState.dataFrameNameState.hasUserEdits) {
      return;
    }

    const currentBlockName = analyticsQueryInfo?.name ?? '';

    if (currentBlockName !== formState.dataFrameNameState.name) {
      formState.setDataFrameNameState({name: currentBlockName, hasUserEdits: false});
    }
  }, [formState, analyticsQueryInfo]);

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    props.onCloseStepInitializationView(...args);
    formState.clearState();
  };

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(props.analysis, props.selectedItem.stepIdToReplace),
    [props.analysis, props.selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);

  const previewDataFrame = getMostRecentData(previewState)?.dataFrame ?? null;

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  // Create component props
  const stepConfigurationViewTemplateProps: Omit<StepConfigurationViewTemplateProps, 'children'> = {
    ...getSharedDataFrameCreationMethodProps(props.selectedItem),
    title: 'Dashboard query',
    clientStateId: props.clientStateId,
    dataFrames:
      previewState.status === AsyncStatus.SUCCESS
        ? [{...previewState.data.dataFrame, name: formState.dataFrameNameState.name}]
        : [],
    disableFinalizeButton:
      previewState.status !== AsyncStatus.SUCCESS ||
      previewState.data.dataFrame == null ||
      (formState.dataFrameNameState.name === '' && formState.dataFrameNameState.hasUserEdits) ||
      (analyticsQueryInfo?.parameterConfigs != null &&
        !areParameterValuesValid(analyticsQueryInfo.parameterConfigs, parameterValues)),
    isFinalizing,
    isLoadingPreview: previewState.status === AsyncStatus.LOADING,
    messages: getErrorAndWarningMessages(previewState),
    noPreviewPlaceholderOptions: {
      title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
      subtitle: 'Select a dashboard and block to see a preview of your table.',
    },
    onCancel,
    onFinalize: finalize,
    toolbar: (
      <ChooseDifferentDataFrameCreationMethodButton
        onOpenStepInitializationView={(newSelectedItem: {
          type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
        }) => {
          props.onOpenStepInitializationView({
            ...newSelectedItem,
            stepIdToReplace: props.selectedItem.stepIdToReplace,
          });
        }}
      />
    ),
  };

  const dashboardAndBlockSelectorFormGroupProps: DashboardAndBlockSelectorFormGroupProps = {
    selectionState: formState.dashboardAndBlockSelectionState,
    onSelect: formState.setDashboardAndBlockSelectionState,
    readOnly: false,
  };

  const dataFrameNameFieldProps: DataFrameNameFieldProps = {
    ...formState.dataFrameNameState,
    onNameChange: (newName: string) => {
      formState.setDataFrameNameState({name: newName, hasUserEdits: true});
    },
    disabled: false,
  };

  const parameterValueFieldListProps: ParameterValueFieldListProps | null =
    analyticsQueryInfo?.parameterConfigs != null && analyticsQueryInfo.parameterConfigs.length > 0
      ? {
          ...makeSharedParameterValueFieldListProps(formState),
          configs: analyticsQueryInfo.parameterConfigs,
          disabled: false,
        }
      : null;

  return (
    <CreateDataFrameFromAnalyticsBlockViewSkeleton
      stepConfigurationViewTemplateProps={stepConfigurationViewTemplateProps}
      dashboardAndBlockSelectorFormGroupProps={dashboardAndBlockSelectorFormGroupProps}
      dataFrameNameFieldProps={dataFrameNameFieldProps}
      parameterValueFieldListProps={parameterValueFieldListProps}
      columnMappingProps={
        props.selectedItem.stepIdToReplace != null
          ? {
              templateDataFrameColumns,
              newDataFrameColumns: previewDataFrame?.columns ?? null,
              newDataFrameState: previewState.status,
              selectedColumns,
              setSelectedColumns,
              isManuallyChanged,
              setIsManuallyChanged,
            }
          : undefined
      }
    />
  );
}
